import { useCallback, useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc';

export interface TableSort {
  /** Backend sort key (must be in the endpoint's allowed-sort whitelist, e.g. 'id' | 'updated_time'). */
  field: string;
  dir: SortDir;
}

export type FilterValue = string | number | undefined | null;

export interface UseServerTableOptions<F extends Record<string, FilterValue>> {
  /** Initial filter values (search text, dropdowns, …). Empty/undefined values are omitted from the query. */
  initialFilters?: F;
  /** Default page size. Default: 10. */
  defaultSize?: number;
  /** Default sort applied on first load. Default: none (backend default applies). */
  defaultSort?: TableSort | null;
}

export interface ServerTable<F extends Record<string, FilterValue>> {
  page: number;
  size: number;
  sort: TableSort | null;
  filters: F;
  /** Serialized `page&size&sort_by&order&<filters>` (empty values omitted) for the list endpoint. */
  queryString: string;
  setPage: (page: number) => void;
  setSize: (size: number) => void;
  /** Set/clear sort. Resets to page 1 (server result set changes). */
  setSort: (sort: TableSort | null) => void;
  /** Merge a partial filter patch. Resets to page 1 (server result set changes). */
  setFilters: (patch: Partial<F>) => void;
  reset: () => void;
}

/** A single Tabulator sorter ({ field, dir }). */
export interface TableSorter {
  field: string;
  dir: SortDir;
}

/**
 * Build a Tabulator `dataSorted` handler that delegates sorting to the server
 * through the given table. `fieldMap` maps the column `field` to the backend's
 * allowed-sort key (e.g. `{ remote_terminal_unit_id: 'id', updated_time: 'updated_time' }`).
 *
 * Two Tabulator quirks are handled because the table data is React-controlled
 * (server-sorted) rather than sorted locally by Tabulator:
 *
 * 1. Empty sorter events are ignored: Tabulator re-fires `dataSorted` with no
 *    sorter when the `data` prop is replaced after a refetch; acting on it would
 *    clear the sort and trigger a no-sort request (undoing the user's sort).
 * 2. The reported direction is unreliable for toggling: replacing the `data` prop
 *    resets Tabulator's per-column sort state, so every header click reports the
 *    starting direction (asc) instead of toggling. We therefore derive the
 *    direction from our own state — same field flips asc⇄desc, a new field uses
 *    the reported (starting) direction.
 */
export function remoteSortHandler<F extends Record<string, FilterValue>>(
  table: ServerTable<F>,
  fieldMap: Record<string, string>
) {
  return (sorters: TableSorter[]) => {
    const s = sorters?.[0];
    if (!s) return;
    const field = fieldMap[s.field];
    if (!field) return;
    const dir: SortDir =
      field === table.sort?.field ? (table.sort.dir === 'asc' ? 'desc' : 'asc') : s.dir;
    table.setSort({ field, dir });
  };
}

const serialize = (obj: Record<string, FilterValue>): string => {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null || value === '') continue;
    sp.set(key, String(value));
  }
  return sp.toString();
};

/**
 * Single source of truth for a server-driven list table (page / size / sort / filters).
 * The page owns what the filter keys mean; this hook only holds the state and builds the
 * query string. Pair it with a React Query hook (`queryParams: table.queryString`) and a
 * `<TablePagination>`. Sort/filter/size changes reset to page 1 so the user never lands on
 * an out-of-range page after the result set changes.
 */
export function useServerTable<F extends Record<string, FilterValue> = Record<string, FilterValue>>(
  options: UseServerTableOptions<F> = {}
): ServerTable<F> {
  const { initialFilters = {} as F, defaultSize = 10, defaultSort = null } = options;

  const [page, setPage] = useState(1);
  const [size, setSizeState] = useState(defaultSize);
  const [sort, setSortState] = useState<TableSort | null>(defaultSort);
  const [filters, setFiltersState] = useState<F>(initialFilters);

  const queryString = useMemo(
    () =>
      serialize({
        ...filters,
        page,
        size,
        sort_by: sort?.field,
        order: sort?.dir,
      }),
    [filters, page, size, sort]
  );

  const setSize = useCallback((next: number) => {
    setSizeState(next);
    setPage(1);
  }, []);

  const setSort = useCallback((next: TableSort | null) => {
    setSortState(next);
    setPage(1);
  }, []);

  const setFilters = useCallback((patch: Partial<F>) => {
    setFiltersState((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }, []);

  const reset = useCallback(() => {
    setPage(1);
    setSizeState(defaultSize);
    setSortState(defaultSort);
    setFiltersState(initialFilters);
  }, [defaultSize, defaultSort, initialFilters]);

  return {
    page,
    size,
    sort,
    filters,
    queryString,
    setPage,
    setSize,
    setSort,
    setFilters,
    reset,
  };
}

export default useServerTable;
